import { safeLocalStorage } from '@toss/storage';
import { getCookie, setCookie } from 'cookies-next';
import type { OptionsType } from 'cookies-next/lib/types';
import { v4 as uuidv4 } from 'uuid';

// ─── Constants ───────────────────────────────────────────────────────────────

export const DEVICE_ID_COOKIE = 'zep_device_id';
export const DEVICE_ID_STORAGE_KEY = '@p@@ze__u_id';
export const CORRELATION_ID_QUERY_PARAM_KEY = 'zepCorrelationId';

const DEVICE_ID_COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year
const DEVICE_ID_VALID_RE = /^[\w./:@=\-+]+$/;

const COOKIE_OPTIONS: OptionsType = {
  path: '/',
  maxAge: DEVICE_ID_COOKIE_MAX_AGE,
  sameSite: 'lax',
};

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DeviceIdResult {
  deviceId: string;
  isNew: boolean;
}

/** cookies-next compatible SSR context */
type ServerSideContext = Pick<OptionsType, 'req' | 'res'>;

// ─── Internal helpers ────────────────────────────────────────────────────────

function isValidDeviceId(value: unknown): value is string {
  // Intentionally lenient: accepts UUIDs, legacy formats, and test values.
  // Rejects empty strings, overly long values, and injection vectors.
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.length <= 128 &&
    DEVICE_ID_VALID_RE.test(value)
  );
}

function getFromStorage(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return safeLocalStorage.get(DEVICE_ID_STORAGE_KEY) ?? undefined;
}

function setToStorage(id: string): void {
  if (typeof window === 'undefined') return;
  safeLocalStorage.set(DEVICE_ID_STORAGE_KEY, id);
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Unified deviceId resolver.
 *
 * CSR: cookie → localStorage → 생성 (양쪽 sync)
 * SSR: cookie에서만 읽기 (없으면 undefined)
 *
 * @param ctx - SSR context ({ req, res }) for cookie access. Omit on CSR.
 */
export function getDeviceId(ctx?: ServerSideContext): string | undefined {
  const isServer = typeof window === 'undefined';

  // 1. Cookie에서 읽기
  const fromCookie = getCookie(DEVICE_ID_COOKIE, (ctx ?? {}) as OptionsType);
  if (isValidDeviceId(fromCookie)) {
    if (!isServer && getFromStorage() !== fromCookie) {
      setToStorage(fromCookie);
    }
    return fromCookie;
  }

  // SSR에서는 cookie 없으면 여기서 종료
  if (isServer) return undefined;

  // 2. CSR: localStorage fallback (기존 ID를 cookie로 마이그레이션)
  const fromStorage = getFromStorage();
  if (isValidDeviceId(fromStorage)) {
    setCookie(DEVICE_ID_COOKIE, fromStorage, COOKIE_OPTIONS);
    return fromStorage;
  }

  // 3. CSR: 새로 생성하고 양쪽에 저장
  const newId = uuidv4();
  setCookie(DEVICE_ID_COOKIE, newId, COOKIE_OPTIONS);
  setToStorage(newId);
  return newId;
}

/**
 * SSR에서 deviceId cookie를 보장합니다.
 * cookie 없으면 UUID 생성 + Set-Cookie 헤더 추가.
 *
 * @example
 * ```ts
 * // getServerSideProps:
 * const { deviceId } = ensureDeviceId(ctx);
 * ```
 */
export function ensureDeviceId(ctx: ServerSideContext): DeviceIdResult {
  const existing = getCookie(DEVICE_ID_COOKIE, ctx as OptionsType);
  if (isValidDeviceId(existing)) {
    return { deviceId: existing, isNew: false };
  }

  const newId = uuidv4();
  const isSecure = process.env.NEXT_PUBLIC_STAGE !== 'LOC';
  setCookie(DEVICE_ID_COOKIE, newId, {
    ...COOKIE_OPTIONS,
    req: ctx.req,
    res: ctx.res,
    secure: isSecure,
  });
  return { deviceId: newId, isNew: true };
}

/**
 * 퀴즈에서 iframe으로 전달된 correlationId를 반환합니다.
 * 로그 추적용으로만 사용하며, deviceId와는 별개입니다.
 */
export const getCorrelationId = (): string | null => {
  if (typeof window === 'undefined') return null;
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(CORRELATION_ID_QUERY_PARAM_KEY);
};
